import { useState, useRef } from 'react';
import { apiConfig } from '../config/api';
import AnimatedContent from './AnimatedContent';

interface PostFormProps {
  userInfo: any;
  anonymousProfile?: any;
  canPost: boolean;
  onPostCreated: () => void;
}

export default function PostForm({ userInfo, anonymousProfile, canPost, onPostCreated }: PostFormProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [diaryText, setDiaryText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!selectedFile || !diaryText.trim()) {
      alert('사진과 일기 내용을 모두 입력해주세요!');
      return;
    }

    if (!canPost) {
      alert('오늘은 이미 게시하셨습니다. 내일 다시 시도해주세요!');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. 업로드 URL 요청
      const uploadUrlResponse = await fetch(apiConfig.endpoints.uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: selectedFile.name })
      });

      if (!uploadUrlResponse.ok) {
        throw new Error('업로드 URL 생성 실패');
      }

      const { upload_url, file_path, required_headers, skip_upload, image_url } = await uploadUrlResponse.json();

      // 2. 파일 업로드 (skip_upload가 false면 실제 업로드)
      if (!skip_upload) {
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadResponse = await fetch(upload_url, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error('파일 업로드 실패');
        }
      }

      // 3. 게시물 생성
      const postResponse = await fetch(apiConfig.endpoints.posts, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Encrypted-Yw-ID': userInfo?.encrypted_yw_id || '',
          'X-Is-Login': userInfo?.encrypted_yw_id ? '1' : '0'
        },
        body: JSON.stringify({
          image_path: file_path,
          diary_text: diaryText,
          display_name: userInfo?.display_name || anonymousProfile?.displayName || '익명',
          photo_url: userInfo?.photo_url || (anonymousProfile?.avatarEmoji ? `emoji:${anonymousProfile.avatarEmoji}` : null),
          avatar_color: anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'
        })
      });

      if (!postResponse.ok) {
        const errorData = await postResponse.json();
        throw new Error(errorData.error || '게시물 등록 실패');
      }

      const postData = await postResponse.json();
      
      // 백엔드에서 생성된 사용자 ID 저장
      if (postData.user_id) {
        localStorage.setItem('current_user_id', postData.user_id);
      }

      // 성공
      alert('게시물이 성공적으로 등록되었습니다! 🎉');
      
      // 폼 초기화
      setSelectedFile(null);
      setPreviewUrl('');
      setDiaryText('');
      setShowForm(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      onPostCreated();

    } catch (error) {
      console.error('게시물 등록 오류:', error);
      alert(error instanceof Error ? error.message : '게시물 등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canPost) {
    return (
      <div className="p-4">
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <div className="text-2xl mb-2">😴</div>
          <p className="text-sm text-gray-600">오늘은 이미 똥을 올렸어요!</p>
          <p className="text-xs text-gray-500">내일 다른 똥을 공유해주세요</p>
        </div>
      </div>
    );
  }

  if (!showForm) {
    return (
      <div className="p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full overflow-hidden">
              {userInfo?.photo_url ? (
                userInfo.photo_url.startsWith('emoji:') ? (
                  <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-sm`}>
                    {userInfo.photo_url.replace('emoji:', '')}
                  </div>
                ) : (
                  <img src={userInfo.photo_url} alt="Profile" className="w-full h-full object-cover" />
                )
              ) : (
                <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-sm`}>
                  {anonymousProfile?.avatarEmoji || (userInfo?.display_name || anonymousProfile?.displayName)?.[0] || 'U'}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="flex-1 text-left px-4 py-3 bg-gray-50 rounded-full text-gray-500 text-sm hover:bg-gray-100 transition-colors"
            >
오늘은 어떤 똥을 쌌나요?
            </button>
          </div>
      </div>
    );
  }

  return (
    <AnimatedContent duration={0.6} direction="vertical" distance={30}>
      <div className="p-4 border-b border-gray-100">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <span className="font-semibold text-gray-900">똥 올리기</span>
            </div>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedFile || !diaryText.trim()}
              className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition-colors disabled:cursor-not-allowed"
            >
              {isSubmitting ? '똥 올리는 중...' : '똥 공유'}
            </button>
          </div>

          {/* User Info */}
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full overflow-hidden">
              {userInfo?.photo_url ? (
                userInfo.photo_url.startsWith('emoji:') ? (
                  <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-sm`}>
                    {userInfo.photo_url.replace('emoji:', '')}
                  </div>
                ) : (
                  <img src={userInfo.photo_url} alt="Profile" className="w-full h-full object-cover" />
                )
              ) : (
                <div className={`w-full h-full bg-gradient-to-br ${anonymousProfile?.avatarColor || 'from-purple-400 to-pink-400'} flex items-center justify-center text-white font-semibold text-sm`}>
                  {anonymousProfile?.avatarEmoji || (userInfo?.display_name || anonymousProfile?.displayName)?.[0] || 'U'}
                </div>
              )}
            </div>
            <div>
              <p className="font-semibold text-gray-900">{userInfo?.display_name || anonymousProfile?.displayName || '익명'}</p>
            </div>
          </div>

          {/* Caption Input */}
          <textarea
            value={diaryText}
            onChange={(e) => setDiaryText(e.target.value)}
            placeholder="오늘의 똥 이야기를 들려주세요..."
            className="w-full p-0 border-none resize-none focus:outline-none text-gray-900 placeholder-gray-500 text-sm"
            rows={3}
            maxLength={500}
          />

          {/* Photo Upload */}
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id="photo-upload"
            />
            
            {previewUrl ? (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full max-h-80 object-cover rounded-lg"
                />
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl('');
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="absolute top-2 right-2 w-8 h-8 bg-black bg-opacity-50 text-white rounded-full flex items-center justify-center hover:bg-opacity-70 transition-opacity"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <label
                htmlFor="photo-upload"
                className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm text-gray-600">똥 사진 선택</p>
              </label>
            )}
          </div>

          {/* Character Count */}
          <div className="flex justify-between items-center text-xs text-gray-400">
            <div></div>
            <span>{diaryText.length}/500</span>
          </div>
        </div>
      </div>
    </AnimatedContent>
  );
}